import React, { useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Real KBT giving channels (from the donate page).
const METHODS = [
  { label: 'Become a Member', note: 'The backbone of KBT.' },
  { label: 'Venmo',  note: '@KehilatBneiTorah' },
  { label: 'Zelle',  note: 'bneitorah.la@gmail.com' },
  { label: 'PayPal', note: 'One-time or recurring.' },
];

function MethodRow({ method, index }) {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-10% 0%' }}
      transition={{ duration: 0.9, delay: 0.12 * index, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        onClick={() => navigate('/donate-now')}
        sx={{
          cursor: 'pointer',
          p: 3,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(6px)',
          border: '1px solid rgba(191, 220, 239, 0.32)',
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 3,
          transition: 'background 350ms ease, border-color 350ms ease, transform 350ms ease',
          '&:hover': {
            background: 'rgba(15,117,188,0.18)',
            borderColor: 'var(--teal-300)',
            transform: 'translateX(-6px)',
          },
        }}
      >
        <Stack spacing={0.6}>
          <Box
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontStyle: 'italic',
              fontSize: 22,
              lineHeight: 1,
            }}
          >
            {method.label}
          </Box>
          <Box sx={{ fontFamily: 'var(--font-body)', fontSize: 13.5, opacity: 0.8 }}>
            {method.note}
          </Box>
        </Stack>
        <Box
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '0.24em',
            color: 'var(--teal-300)',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          Give →
        </Box>
      </Box>
    </motion.div>
  );
}

function CounterCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        sx={{
          mt: { xs: 6, md: 8 },
          p: { xs: 3, md: 4 },
          border: '1.5px solid rgba(212,224,239,0.35)',
          background: 'rgba(7,6,26,0.4)',
          backdropFilter: 'blur(6px)',
          maxWidth: 720,
        }}
      >
        <Box
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
            color: 'var(--teal-300)',
            mb: 1.5,
          }}
        >
          The Mission, in numbers
        </Box>
        <Box
          sx={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 'clamp(36px, 4.8vw, 60px)',
            lineHeight: 1.05,
            color: '#fff',
          }}
        >
          1,000,000
          <span style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--teal-300)' }}>
            {' '}hours of Torah
          </span>
        </Box>
        <Box
          sx={{
            mt: 1.5,
            fontFamily: 'var(--font-body)',
            fontSize: 15,
            color: 'rgba(255,255,255,0.85)',
            maxWidth: 540,
          }}
        >
          To share across Los Angeles, over the next decade. Every shiur counted,
          every page logged — your support is what keeps the doors open.
        </Box>
      </Box>
    </motion.div>
  );
}

export default function DuskInvitation() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // White → navy wash as you scroll.
  const bg = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [
      'linear-gradient(180deg, #ffffff 0%, #ffffff 70%, #ffffff 100%)',
      'linear-gradient(180deg, #ffffff 0%, #2c3a78 70%, #141238 100%)',
      'linear-gradient(180deg, #141238 0%, #07061A 70%, #07061A 100%)',
    ]
  );
  const textColor = useTransform(
    scrollYProgress,
    [0, 0.5, 0.7, 1],
    ['#07061A', '#07061A', '#FFFFFF', '#FFFFFF']
  );
  const subtleColor = useTransform(
    scrollYProgress,
    [0, 0.6, 1],
    ['#5D5D9C', '#BFDCEF', '#D5D8EC']
  );
  const tealGlow = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <motion.section
      ref={ref}
      style={{
        position: 'relative',
        padding: '160px 0 160px',
        background: bg,
        overflow: 'hidden',
        color: textColor,
      }}
    >
      <motion.div
        style={{
          position: 'absolute',
          top: '50%',
          right: '-12%',
          width: '60vw',
          maxWidth: 700,
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(63,149,206,0.45) 0%, rgba(15,117,188,0.18) 40%, transparent 70%)',
          opacity: tealGlow,
          filter: 'blur(20px)',
          translateY: '-50%',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', maxWidth: 1280, mx: 'auto', px: { xs: 3, md: 6 } }}>
        <motion.div style={{ color: subtleColor }}>
          <Box className="kicker">Help KBT Spread Torah</Box>
        </motion.div>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' },
            gap: { xs: 8, md: 10 },
            alignItems: 'start',
          }}
        >
          <Box>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-15% 0%' }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontWeight: 700,
                fontSize: 'clamp(44px, 7.4vw, 132px)',
                lineHeight: 0.94,
                letterSpacing: '-0.025em',
              }}
            >
              A million hours
              <br />
              <span style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--teal-300)' }}>
                won't fund themselves.
              </span>
            </motion.h2>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.3 }}
              style={{ marginTop: 40, maxWidth: 540 }}
            >
              <Box
                sx={{
                  fontFamily: 'var(--font-body)',
                  fontWeight: 400,
                  fontSize: 'clamp(17px, 1.5vw, 21px)',
                  lineHeight: 1.55,
                  color: 'inherit',
                  opacity: 0.88,
                }}
              >
                We're on a mission to share one million hours of Torah-learning
                across Los Angeles, and to build a kehilla that other communities
                can model themselves on. Membership is the backbone — every gift
                keeps the lights on, the kettle boiling, and the seder running.
              </Box>
            </motion.div>

            <CounterCard />
          </Box>

          <Box>
            <Stack spacing={2.5}>
              {METHODS.map((m, i) => (
                <MethodRow key={m.label} method={m} index={i} />
              ))}
            </Stack>
          </Box>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
          sx={{ mt: 12 }}
        >
          <motion.div style={{ color: subtleColor }}>
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
              }}
            >
              Drop a check in person  ·  9017 W Pico Blvd  ·  LA
            </Box>
          </motion.div>
          <GiveButton />
        </Stack>
      </Box>
    </motion.section>
  );
}

function GiveButton() {
  const navigate = useNavigate();
  return (
    <Box
      onClick={() => navigate('/donate-now')}
      sx={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        px: 4,
        py: 2.25,
        background: 'var(--teal-500)',
        color: '#fff',
        fontFamily: 'var(--font-body)',
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        borderRadius: 999,
        transition: 'background 350ms ease, transform 350ms ease',
        '&:hover': { background: 'var(--teal-300)', transform: 'translateY(-2px)' },
      }}
    >
      Donate now
    </Box>
  );
}
